import FormField from 'components/FormField';
import {
  AiModelState,
  getPrefixName,
  hideRadioClassName,
  Inline,
  Prefix,
  stores2storeScopes,
} from './share';
import { InputNumber, Radio, Select } from 'antd';
import { useCallback, useMemo } from 'react';
import { updateWarranty } from 'views/BootGuide/service';
import { getStoreCount } from 'components/StoreScopes';
import useSmartField from 'hooks/useSmartField';
import styled from '@emotion/styled';
import ModalMode from './ModalMode';
import { useTranslate } from '@tolgee/react';

type FV = BootGuide.AiModel.FormValues.Warranty;

export function useLabel() {
  const { t } = useTranslate();

  return useMemo(() => {
    return {
      noSupport: t('本店商品无保修服务'),
      support: t('本店商品有保修服务'),
    } as const;
  }, [t]);
}

export const useDefineLabel = () => {
  const { t } = useTranslate();
  return useCallback(
    (value: FV) => {
      if (value.value === 'noSupport') {
        return t('本店商品无保修服务');
      } else {
        return t('保修期{{X}}', {
          X: `${value.number} ${value.unit === 'MONTH' ? t('月') : t('年')}`,
        });
      }
    },
    [t],
  );
};

function Warranty({ prefix, singleMode }: { prefix?: Prefix; singleMode?: boolean }) {
  const label = useLabel();

  const {
    field: { value, onChange },
  } = useSmartField<FV['value']>(getPrefixName('value', prefix));
  const {
    field: { value: unit, onChange: onUnitChange },
  } = useSmartField<FV['unit']>(getPrefixName('unit', prefix));

  const getHideStyle = (type: FV['value']) =>
    singleMode && (value !== type || type === 'support') ? { display: 'none' } : undefined;

  return (
    <div>
      {Object.keys(label).map((key) => {
        return (
          <div key={key} className="mb-2 mt-[6px]" style={getHideStyle(key)}>
            <Radio
              className={singleMode ? hideRadioClassName : undefined}
              checked={value === key}
              onChange={({ target: { checked } }) => {
                checked && onChange(key);
                if (key === 'support' && !unit) {
                  onUnitChange('YEAR');
                }
              }}
            >
              {label[key]}
            </Radio>
          </div>
        );
      })}
      <TimeContainer background="transparent">
        <Time prefix={prefix} noStyle={singleMode} />
      </TimeContainer>
    </div>
  );
}

export function Time({ prefix, noStyle = false }: { prefix?: Prefix; noStyle?: boolean }) {
  const { t } = useTranslate();

  const {
    field: { value },
  } = useSmartField<FV['value']>(getPrefixName('value', prefix));
  const {
    field: { value: storeScopes },
  } = useSmartField<FV['storeScopes']>(getPrefixName('storeScopes', prefix));
  const {
    field: { value: unit, onChange: onUnitChange },
  } = useSmartField<FV['unit']>(getPrefixName('unit', prefix));

  if (value !== 'support' || !getStoreCount(storeScopes)) {
    return <></>;
  }

  return (
    <FormField<FV['number']>
      name={getPrefixName('number', prefix)}
      required
      label={t('保修期')}
      validate={(value) => (value ? undefined : t('department.请输入x', { x: t('保修期') }))}
      noStyle={noStyle}
    >
      {({ field }) => {
        return (
          <Inline>
            <InputNumber min={1} max={99} precision={0} {...(field as any)} />
            <Select
              className="!w-auto"
              dropdownMatchSelectWidth={false}
              value={unit}
              onChange={onUnitChange}
              options={[
                { value: 'YEAR', label: t('年') },
                { value: 'MONTH', label: t('月') },
              ]}
            />
          </Inline>
        );
      }}
    </FormField>
  );
}

export function WarrantyBootGuide(props: AiModelState<FV>) {
  const label = useLabel();

  return (
    <ModalMode
      state={props}
      title={props.t('保修服务')}
      tips={`${props.t('我还需要了解一些您的「商品」')}\n${props.t('您的店铺是否有保修服务?')}`}
      form={<Warranty />}
      getFirstInitialValues={useCallback(({ stores }): { values: FV[] } => {
        return {
          values: [
            {
              value: 'noSupport',
              storeScopes: stores2storeScopes(stores),
              number: 1,
              unit: 'YEAR',
            },
            { value: 'support', storeScopes: [], number: 1, unit: 'YEAR' },
          ],
        };
      }, [])}
      updateApi={updateWarranty}
      getFirstFormItem={({ value }, index, storeSelect) => {
        return (
          <div>
            <section>
              {label[value]}
              {storeSelect}
            </section>
            <TimeContainer background="#fff">
              <Time prefix={['values', index]} />
            </TimeContainer>
          </div>
        );
      }}
      getFirstFormStoreSelectKey={({ value }) => value}
    />
  );
}

export const TimeContainer = styled.div<{ background: string }>`
  .ant-form-item-label > label {
    height: 40px;
    padding: 4px 0 4px 12px;
    background: ${({ background }) => background};
  }
  .ant-form-item-control > .ant-form-item-control-input {
    padding: 4px 0;
    background: ${({ background }) => background};
  }
`;

export default Warranty;
